/**
 * Clip Data Table Component
 * Displays tabular view of clip measurements
 */

import React, { useState } from 'react';
import { ClipDataPoint } from '../../lib/api-clip-monitor';
import { DiaperModel } from '../../lib/types';
import { calculateWetnessVolume } from '../../lib/utils';
import { format } from 'date-fns';

interface ClipDataTableProps {
  data: ClipDataPoint[];
  diaperModel: DiaperModel | null;
}

// Status codes from BleAd.java; lifecycle role from insert_packet.sql
const StatusCodeLabels: Record<number, { label: string; color: string; lifecycle: string }> = {
  0:   { label: 'Attached',           color: 'bg-green-100 text-green-800',  lifecycle: 'Opens diaper' },
  1:   { label: 'Detached',           color: 'bg-gray-100 text-gray-800',    lifecycle: 'Closes diaper' },
  2:   { label: 'Shorted',            color: 'bg-yellow-100 text-yellow-800', lifecycle: 'Attached, no wetness' },
  3:   { label: 'Same Wet Reattach',  color: 'bg-blue-100 text-blue-800',    lifecycle: 'Opens diaper' },
  4:   { label: 'Wet Diaper Attached', color: 'bg-blue-100 text-blue-800',   lifecycle: 'Opens diaper' },
  5:   { label: 'Leak Imminent',      color: 'bg-red-100 text-red-800',      lifecycle: 'Opens diaper' },
  6:   { label: 'Identifying',        color: 'bg-gray-100 text-gray-800',    lifecycle: 'Closes diaper' },
  7:   { label: 'Reboot Wet',         color: 'bg-purple-100 text-purple-800', lifecycle: 'No change' },
  8:   { label: 'Reboot Recovered',   color: 'bg-purple-100 text-purple-800', lifecycle: 'No change' },
  9:   { label: 'Entering Sleep',     color: 'bg-indigo-100 text-indigo-800', lifecycle: 'No change' },
  30:  { label: 'Pooling',            color: 'bg-orange-100 text-orange-800', lifecycle: 'Opens diaper' },
  255: { label: 'Reboot Dry',         color: 'bg-purple-100 text-purple-800', lifecycle: 'No change' },
  '-1': { label: 'Reboot Dry',        color: 'bg-purple-100 text-purple-800', lifecycle: 'No change' },
};

export const ClipDataTable: React.FC<ClipDataTableProps> = ({ data, diaperModel }) => {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [showLatest, setShowLatest] = useState(20);

  const displayData = data.slice(-showLatest).reverse();

  // Calculate wetness percentage
  const calculateWetnessPercent = (wetnessLevel: number): number => {
    if (!diaperModel || !diaperModel.capacity) return 0;
    const volumeData = calculateWetnessVolume(wetnessLevel, diaperModel);
    const volume = volumeData.volume || 0;
    return Math.round((volume / diaperModel.capacity) * 100);
  };

  const toggleRow = (index: number) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const getStatusInfo = (statusCode: number) => {
    return StatusCodeLabels[statusCode] || {
      label: `Unknown (${statusCode})`,
      color: 'bg-red-100 text-red-800',
      lifecycle: 'Unknown',
    };
  };

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
        <p className="text-4xl mb-2">📋</p>
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-bold">
          Measurement Log - All Packets ({data.length} total records)
        </h3>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Show latest:</label>
          <select
            value={showLatest}
            onChange={(e) => setShowLatest(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={data.length}>All</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Time</th>
              <th className="px-4 py-2 text-left font-semibold">Wetness</th>
              <th className="px-4 py-2 text-left font-semibold">Status</th>
              <th className="px-4 py-2 text-left font-semibold">Counter</th>
              <th className="px-4 py-2 text-left font-semibold">ADC Values</th>
              <th className="px-4 py-2 text-left font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((record, index) => {
              const statusInfo = getStatusInfo(record.parsed.statusCode);
              const isExpanded = expandedRow === index;

              return (
                <React.Fragment key={index}>
                  <tr
                    className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                      isExpanded ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => toggleRow(index)}
                  >
                    <td className="px-4 py-2 font-mono text-xs">
                      {format(new Date(record.timestamp), 'HH:mm:ss.SSS')}
                    </td>
                    <td className="px-4 py-2">
                      <div className="font-semibold">
                        {record.parsed.wetnessLevel.toLocaleString()}
                      </div>
                      {diaperModel && (
                        <div className="text-xs text-gray-600">
                          ({calculateWetnessPercent(record.parsed.wetnessLevel)}%)
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.color}`}
                        title={`Status ${record.parsed.statusCode} — ${statusInfo.lifecycle}`}
                      >
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-2">{record.parsed.diaperCounter}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      [{record.parsed.adcValues.join(', ')}]
                    </td>
                    <td className="px-4 py-2 text-gray-400">
                      {isExpanded ? '▼' : '▶'}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-blue-50 border-b border-blue-100">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <p className="font-semibold mb-1">Timing</p>
                            <p>
                              <span className="text-gray-600">Received:</span>{' '}
                              {format(
                                new Date(record.timestamp),
                                'yyyy-MM-dd HH:mm:ss.SSS'
                              )}
                            </p>
                            <p>
                              <span className="text-gray-600">Payload:</span>{' '}
                              {format(
                                new Date(record.payloadTime),
                                'yyyy-MM-dd HH:mm:ss.SSS'
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="font-semibold mb-1">Relay Info</p>
                            <p>
                              <span className="text-gray-600">Address:</span>{' '}
                              {record.relayAddress || '—'}
                            </p>
                            <p>
                              <span className="text-gray-600">Android ID:</span>{' '}
                              {record.relayAndroidId || '—'}
                            </p>
                          </div>
                          <div>
                            <p className="font-semibold mb-1">Parsed Payload</p>
                            <p>
                              <span className="text-gray-600">FW Rev:</span> {record.parsed.firmwareRevision}
                            </p>
                            <p>
                              <span className="text-gray-600">Status Code:</span> {record.parsed.statusCode}
                            </p>
                            <p>
                              <span className="text-gray-600">Wetness:</span> {record.parsed.wetnessLevel.toLocaleString()}
                            </p>
                            <p>
                              <span className="text-gray-600">Diaper Model:</span> {record.parsed.diaperModel}
                            </p>
                            <p>
                              <span className="text-gray-600">Diaper Counter:</span> {record.parsed.diaperCounter}
                            </p>
                            <p>
                              <span className="text-gray-600">Battery:</span> {record.parsed.batteryPercent}%
                            </p>
                          </div>
                          <div>
                            <p className="font-semibold mb-1">
                              ADC Values (5 Channels)
                            </p>
                            <p className="font-mono">
                              [{record.parsed.adcValues.join(', ')}]
                            </p>
                            <p className="font-semibold mt-2 mb-1">Raw Payload (13 bytes)</p>
                            <p className="font-mono break-all">
                              [{record.payload.join(', ')}]
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

